<script setup lang="ts">
import { defineEmits, defineProps } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'

import { FavoriteButton } from '@haskoning/vue-component-library'

import { useCalculationStore } from '@/stores/calculation'
import DropdownMenu from '@/components/form/DropdownMenu.vue'
import FilterPanel from '@/components/calculation-result/filter/FilterPanel.vue'

const props = defineProps({
  itemsLength: {
    type: Number,
    required: true
  }
})

const emit = defineEmits(['apply-filter', 'clear-all-filter'])

const router = useRouter()

const calculationStore = useCalculationStore()
const crossSectionStore = useCrossSectionStore()

const { currentItem, writeLoading, exportLoading } = storeToRefs(calculationStore)
const { count: crossSectionCount } = storeToRefs(crossSectionStore)

const formatDate = (dateString?: string) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const toggleFavorite = async () => {
  if (currentItem.value) {
    currentItem.value.favorite = !currentItem.value.favorite
    await calculationStore.update(currentItem.value)
  }
}

const navigateToProject = () => {
  router.push({ name: 'project-detail' })
}

const navigateToInput = () => {
  if (currentItem.value) {
    router.push({
      name: 'calculation-form',
      params: {
        id: currentItem.value.project,
        calculationId: currentItem.value.id
      }
    })
  }
}

const resetCalculation = async () => {
  if (currentItem.value) {
    const response = await calculationStore.duplicate(currentItem.value)
    if (response) {
      await router.push({
        name: 'calculation-form',
        params: { calculationId: response.id }
      })
    }
  }
}

const handleExcelExport = async () => {
  if (currentItem.value) {
    await calculationStore.downloadExcel(currentItem.value.id)
  }
}
</script>

<template>
  <v-card class="mx-2 py-4" elevation="0" flat>
    <v-row no-gutters class="align-center">
      <v-col class="text-center">
        <v-card-title class="header-font d-flex align-center justify-center">
          <span>
            Results for {{ currentItem?.name }}, number {{ currentItem?.id }} -
            {{ formatDate(currentItem?.created) }}
          </span>
          <FavoriteButton :item="currentItem" @update-favorite="toggleFavorite" />
        </v-card-title>
        <v-card-subtitle class="description-font mb-6">
          The calculation yielded <strong>{{ crossSectionCount }}</strong> valid cross sections,
          showing
          <strong>{{ itemsLength }} results.</strong>
        </v-card-subtitle>
      </v-col>
    </v-row>
  </v-card>

  <v-card
    class="d-flex flex-wrap align-center justify-center mx-2 py-4"
    color="#F6F6F6"
    elevation="0"
    flat
  >
    <v-card-actions>
      <v-btn
        class="text-none px-4"
        variant="tonal"
        prepend-icon="mdi-arrow-left"
        @click="navigateToProject"
      >
        Back to project
      </v-btn>
      <v-btn
        class="text-none px-4"
        variant="tonal"
        prepend-icon="mdi-text-box-check-outline"
        @click="navigateToInput"
      >
        View input
      </v-btn>
      <v-btn
        class="text-none px-4"
        variant="tonal"
        prepend-icon="mdi-restart"
        :loading="writeLoading"
        @click="resetCalculation"
      >
        Reset calculation
      </v-btn>
      <v-btn
        class="text-none px-4"
        variant="tonal"
        :disabled="exportLoading"
        prepend-icon="mdi-tray-arrow-down"
        :loading="exportLoading"
        @click="handleExcelExport"
      >
        Export to Excel
      </v-btn>
    </v-card-actions>
    <DropdownMenu
      iconColor="primary"
      activeIcon="mdi-filter-check"
      inactiveIcon="mdi-filter-check-outline"
      activeText="active filters"
      inactiveText="Select your filters"
    >
      <FilterPanel
        @apply-filter="emit('apply-filter')"
        @clear-all-filter="emit('clear-all-filter')"
      />
    </DropdownMenu>
  </v-card>
</template>

<style scoped></style>
